import { IMAGE_MAX_BYTES, IMAGE_MAX_SIDE_PX } from "../../shared/limits.ts";

/**
 * Client-side image pipeline (03-architecture.md §Images): decode → resize so
 * the longest side ≤ 2,000 px → WebP at 0.82, retry at 0.7 if > 300 KB,
 * JPEG fallback when the browser cannot encode WebP.
 */
export async function compressImage(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, IMAGE_MAX_SIDE_PX / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas");
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  const encode = (type: string, q: number) =>
    new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), type, q));
  for (const [type, q] of [
    ["image/webp", 0.82],
    ["image/webp", 0.7],
    ["image/webp", 0.55],
    ["image/jpeg", 0.8],
    ["image/jpeg", 0.65],
  ] as const) {
    const blob = await encode(type, q);
    if (!blob || blob.type !== type) continue;
    if (blob.size <= IMAGE_MAX_BYTES) return blob;
  }
  throw new Error("too_large");
}
